'use client';

import React from 'react';
import Modal from '@/components/ui/modal';
import { CheckCircle, XCircle, AlertCircle, Clock, Receipt, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import type { OrderReadyToSubmit } from '@/components/order/order.types';
import { parseOrderId } from '@/utils/formatters';

export interface OrderFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  status: 'loading' | 'success' | 'error';
  orderId?: string;
  errorMessage?: string;
  onRetry?: () => void;
  onGoToHome?: () => void;
  orderData?: OrderReadyToSubmit;
}

const OrderFeedbackModal: React.FC<OrderFeedbackModalProps> = ({
  isOpen,
  onClose,
  status,
  orderId,
  errorMessage,
  onRetry,
  onGoToHome,
  orderData,
}) => {
  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center py-8">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              className="w-16 h-16 mx-auto mb-6">
              <Clock className="w-16 h-16 text-primary" />
            </motion.div>
            <h3 className="text-2xl font-bold text-foreground mb-3">Submitting Your Order</h3>
            <p className="text-muted-foreground mb-6">
              Please wait while we process your order. This may take a few moments.
            </p>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-8">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
              className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}>
              <h3 className="text-2xl font-bold text-foreground mb-3">Order Submitted!</h3>
              <p className="text-lg text-muted-foreground mb-2">
                Thank you for your order. We&apos;ve received it successfully.
              </p>
              {orderId && (
                <div className="border border-accent/30 mb-12">
                  <p className="text-sm text-muted-foreground mb-1">Order ID</p>
                  <p className="text-xl font-mono font-semibold text-foreground">{orderId}</p>
                </div>
              )}
              {/* <div className="space-y-3 text-left bg-muted/30 rounded-lg p-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <p className="text-sm text-muted-foreground">You&apos;ll receive an email confirmation shortly</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <p className="text-sm text-muted-foreground">
                    We&apos;ll notify you when your order is being prepared
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <p className="text-sm text-muted-foreground">Expected delivery time will be confirmed via SMS</p>
                </div>
              </div> */}
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-3 justify-center">
              {onGoToHome && (
                <Button variant="secondary" onClick={onGoToHome} className="flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Home
                </Button>
              )}

              <Button
                // onClick={handleDownloadReceipt}
                onClick={() => window.open(`/receipt-confirmation/${parseOrderId(orderId)}`)}
                disabled={!orderId || !orderData}
                className="flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                View Receipt Confirmation
              </Button>
            </motion.div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center py-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.4, type: 'spring', stiffness: 200 }}
              className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-600" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}>
              <h3 className="text-2xl font-bold text-foreground mb-3">Order Submission Failed</h3>
              <p className="text-muted-foreground mb-2">We encountered an issue while processing your order.</p>

              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{errorMessage}</p>
                  </div>
                </div>
              )}

              <div className="space-y-3 text-left bg-muted/30 rounded-lg p-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
                  <p className="text-sm text-muted-foreground">Please check your internet connection</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
                  <p className="text-sm text-muted-foreground">Verify all required information is filled</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
                  <p className="text-sm text-muted-foreground">Try again or contact support if the issue persists</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              {onRetry && (
                <Button onClick={onRetry} className="bg-primary hover:bg-primary/90">
                  Try Again
                </Button>
              )}
            </motion.div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={status === 'loading' ? () => {} : onClose} // Prevent closing during loading
      size="lg"
      showCloseButton={status !== 'loading'}
      closeOnBackdropClick={status !== 'loading'}
      closeOnEscape={status !== 'loading'}>
      {renderContent()}
    </Modal>
  );
};

export default OrderFeedbackModal;
